<template>
    <div class="payment-item" :class="{ 'has-discount': item.orderItem.hasDiscount }">
        <OrderItemQuantity class="quantity" :quantity="item.quantity" :quantityDigits="quantityDigits" />

        <div class="item-details-container">
            <div class="main-details">
                <span class="name">{{ getName(item.orderItem, props.showInternalNames) }}</span>
                <ItemPrice
                    :hasDiscount="item.orderItem.hasDiscount"
                    :quantity="item.quantity"
                    :singlePrice="item.orderItem.singlePrice"
                    :singlePriceAfterDiscount="item.orderItem.singlePriceAfterDiscount"
                    :discountType="item.orderItem.lastDiscountType"
                />
            </div>

            <div class="subs" v-if="item.orderItem.options.length">
                <OrderItemOptionGroup
                    v-for="optionGroup in item.orderItem.options"
                    :key="optionGroup.id"
                    :optionGroup="optionGroup"
                    :multiplier="item.quantity"
                    :showInternalNames="props.showInternalNames"
                    :alwaysShowGroupName="props.alwaysShowGroupName"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import type { ICustomerPaymentItem } from "@/data/model/CustomerPayment";
    import { getName } from "@/shared/helpers/ItemNameHelper";

    import ItemPrice from "@/components/order/ItemPrice.vue";
    import OrderItemOptionGroup from "@/components/order/OrderItemOptionGroup.vue";
    import OrderItemQuantity from "@/components/order/OrderItemQuantity.vue";

    // define properties
    const props = defineProps<{
        item: ICustomerPaymentItem;
        quantityDigits?: number;
        showInternalNames: boolean;
        alwaysShowGroupName?: boolean;
    }>();
</script>

<style scoped lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    .payment-item {
        font-size: 16px;
        padding: 1.25em 0;
        position: relative;
        border-bottom: 1px solid colors.$lightGreyBorder;
        font-weight: 300;
        line-height: 1.25em;
        color: colors.$fontColor;
        display: flex;
        position: relative;

        &.has-discount {
            padding-top: 1.725em;

            .main-details {
                margin-bottom: -0.625em;
            }
        }

        > .item-details-container {
            flex: 1 1 auto;
            color: colors.$fontColor;
            transition: color 0.2s variables.$easeOutExpo;

            &.inactive {
                color: colors.$lightFontColor;
            }

            > .main-details {
                display: flex;
                align-items: flex-start;

                .name {
                    text-overflow: ellipsis;
                    flex: 1 1 auto;
                    word-break: break-word;
                }

                .price {
                    flex: 0 0 auto;

                    &.has-discount {
                        position: relative;
                        top: -0.625em;
                    }
                }
            }

            > .subs {
                padding: 0.375em 0.125em 0.5em;
                margin-bottom: -0.75em;
            }
        }
    }
</style>
